import type { RouteRecord } from "vite-react-ssg";
import { Outlet } from "react-router-dom";
import { useEffect } from "react";
import i18n from "./shared/config/i18n";
import type { Lang } from "./shared/i18n/lang";
import { useMetrika } from "./shared/lib/useMetrika";
import { IconSprite } from "./shared/ui/Icon";
import { useRevealObserver } from "./shared/motion";
import { MainLayout } from "./pages/landing";
import PrivacyPage from "./pages/business-app/privacy";
import BusinessTerms from "./pages/business-app/ui/BusinessTerms";
import ClientAppTerms from "./pages/client-app/ui/ClientAppTerms";
import ClientAppPrivacy from "./pages/client-app/ui/ClientAppPrivacy";
import { NotFoundPage } from "./pages/not-found";

// Sets the i18n language for the whole subtree. Done synchronously (not in an effect) so
// SSR/prerender emits the correct language; the effect handles client-side navigation.
function LangLayout({ lng }: { lng: Lang }) {
  if (i18n.language !== lng) i18n.changeLanguage(lng);
  useEffect(() => {
    if (i18n.language !== lng) i18n.changeLanguage(lng);
  }, [lng]);
  // Yandex Metrica: init + per-route pageview tracking for the whole app subtree.
  useMetrika();
  // Reveal-on-scroll + `ns:play` for every page; also tells the head-script fail-safe JS is up.
  useRevealObserver();
  return (
    <>
      <IconSprite />
      <Outlet />
    </>
  );
}

// The same page tree is mounted under both the default (uz) root and the /ru prefix,
// giving every page a distinct per-language URL for hreflang/SEO.
const pages = (): RouteRecord[] => [
  { index: true, element: <MainLayout /> },
  { path: "business-app/privacy", element: <PrivacyPage /> },
  { path: "business-app/terms", element: <BusinessTerms /> },
  { path: "client-app/privacy", element: <ClientAppPrivacy /> },
  { path: "client-app/terms", element: <ClientAppTerms /> },
  // Unknown paths. Prerendered as /404 and /ru/404 (ssgOptions in vite.config.ts).
  { path: "*", element: <NotFoundPage /> },
];

export const routes: RouteRecord[] = [
  { path: "/", element: <LangLayout lng="uz" />, children: pages() },
  { path: "/ru", element: <LangLayout lng="ru" />, children: pages() },
];
